import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * tabbed-content — a tabbed showcase. A tab bar switches between panels; each `tab` block is an
 * image + content split (heading / subheading / body / button) with an optional product mini-card —
 * bound to a REAL catalog product via a product picker (image / name / price / link resolve live).
 * Reproduces patterns like "Trusted by athletes" (Marathon / Trail / Triathlon tabs, each an athlete
 * feature) and works for any tabbed feature/story content.
 *
 * SSR-safe & no-JS: the tab bar is pure CSS (visually-hidden radios + `:checked ~ panel` reveal), so it
 * switches with JavaScript fully disabled and the first tab shows by default.
 */
export const tabbedContentSchema = {
    type: 'tabbed-content',
    name: 'Tabbed content',
    version: 1,
    templates: ['index', 'collection', 'page', 'product', 'thank-you'],
    fields: [
        // ── Content ───────────────────────────────────────────────────────────────
        { id: 'heading', type: 'text', label: 'Section heading', group: 'Content', maxLength: 120 },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h1',
            options: [
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
            ],
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: 'Content',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
        },
        // ── Tabs ────────────────────────────────────────────────────────────────
        {
            id: 'tabsAlignment',
            type: 'select',
            label: 'Tab bar alignment',
            group: 'Tabs',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
        },
        // ── Section (universal) ──────────────────────────────────────────────────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [
        {
            type: 'tab',
            name: 'Tab',
            max: 8,
            fields: [
                { id: 'label', type: 'text', label: 'Tab label', default: 'Tab', maxLength: 40 },
                { id: 'image', type: 'image', label: 'Feature image', group: 'Media' },
                {
                    id: 'imagePosition',
                    type: 'select',
                    label: 'Image position',
                    group: 'Media',
                    default: 'right',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'right', label: 'Right' },
                    ],
                },
                { id: 'heading', type: 'text', label: 'Heading', group: 'Content', maxLength: 120 },
                { id: 'subheading', type: 'text', label: 'Subheading', group: 'Content', maxLength: 160 },
                { id: 'text', type: 'richtext', label: 'Body', group: 'Content' },
                { id: 'buttonLabel', type: 'text', label: 'Button label', group: 'Content', maxLength: 40 },
                { id: 'buttonLink', type: 'link', label: 'Button link', group: 'Content' },
                // Optional product mini-card (thumbnail + name + price → product link). Preferred authoring path:
                // PICK one of the store's real products — the card then reads its cover image, name, formatted price
                // and product URL live from the catalog (no re-typing, no stale price, the link always points at the
                // right PDP). The four manual fields below stay for documents authored before the picker (and for a
                // card that isn't a catalog product); they are used ONLY while nothing is picked, and the inspector
                // hides them as soon as a product is chosen so no field is silently dead.
                {
                    id: 'showProduct',
                    type: 'checkbox',
                    label: 'Show product card',
                    group: 'Product card',
                    default: false,
                },
                {
                    id: 'product',
                    type: 'product-picker',
                    label: 'Product',
                    group: 'Product card',
                    info: 'Pick one of your products — its image, name, price and link fill in automatically.',
                    visibleWhen: { field: 'showProduct', equals: true },
                },
                {
                    id: 'productImage',
                    type: 'image',
                    label: 'Product image',
                    group: 'Product card',
                    info: 'Used only while no product is selected above.',
                    visibleWhen: [
                        { field: 'showProduct', equals: true },
                        { field: 'product', empty: true },
                    ],
                },
                {
                    id: 'productName',
                    type: 'text',
                    label: 'Product name',
                    group: 'Product card',
                    maxLength: 80,
                    visibleWhen: [
                        { field: 'showProduct', equals: true },
                        { field: 'product', empty: true },
                    ],
                },
                {
                    id: 'productPrice',
                    type: 'text',
                    label: 'Product price',
                    group: 'Product card',
                    maxLength: 40,
                    visibleWhen: [
                        { field: 'showProduct', equals: true },
                        { field: 'product', empty: true },
                    ],
                },
                {
                    id: 'productLink',
                    type: 'link',
                    label: 'Product link',
                    group: 'Product card',
                    visibleWhen: [
                        { field: 'showProduct', equals: true },
                        { field: 'product', empty: true },
                    ],
                },
            ],
        },
    ],
    maxBlocks: 8,
};
export const tabbedContentPresets = [
    {
        name: 'Tabbed content',
        settings: {
            heading: '@t:section.tabs_heading',
            headingSize: 'h1',
            headingAlignment: 'left',
            tabsAlignment: 'left',
            colorScheme: 'light',
            paddingTop: 64,
            paddingBottom: 64,
        },
        blocks: [
            {
                type: 'tab',
                settings: {
                    label: '@t:section.tab1_label',
                    imagePosition: 'right',
                    heading: '@t:section.tab1_heading',
                    subheading: '@t:section.tab_subheading',
                    text: '@t:section.tab1_text',
                    buttonLabel: '@t:common.learn_more',
                },
            },
            {
                type: 'tab',
                settings: {
                    label: '@t:section.tab2_label',
                    imagePosition: 'right',
                    heading: '@t:section.tab2_heading',
                    subheading: '@t:section.tab_subheading',
                    text: '@t:section.tab2_text',
                    buttonLabel: '@t:common.learn_more',
                },
            },
            {
                type: 'tab',
                settings: {
                    label: '@t:section.tab3_label',
                    imagePosition: 'right',
                    heading: '@t:section.tab3_heading',
                    subheading: '@t:section.tab_subheading',
                    text: '@t:section.tab3_text',
                    buttonLabel: '@t:common.learn_more',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const tabbedContentMeta = {
    label: 'Tabbed content',
    group: 'Content',
    icon: 'view-column',
};
//# sourceMappingURL=schema.js.map